import { useState, useEffect, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { get, isEmpty, chain, isUndefined, noop, map, includes, replace, isEqual } from "lodash"
import { Card, Paper, IconButton, Divider, Badge, ClickAwayListener, Alert } from "@mui/material"
import { Refresh as RefreshIcon, FilterList } from "@mui/icons-material"
import DateSelector from "components/date-picker"
import RebalanceTrades from "modules/rebalance-trades"
import Table from "components/securities-table/table"
import AssetsTableHeader from "components/assets-table-header"
import SaveExperiment from "modules/save-experiment"
import Error from "components/error"
import EditForecast from "./components/edit-forecast"
import ForecastSelector from "./components/forecast-selector"
import TradesButton from "./components/trades-button"
import SaveExperimentButton from "components/save-experiment-button"
import NoData from "components/intraday/no-data"
import ShowNotHeldButton from "./components/show-notHeld-button"
import ResetButton from "./components/reset-button"
import IntradayRefresh from "components/intraday/refresh"
import IntradayRebalanceReload from "modules/intraday-rebalance-reload"
import OPTooltip from "components/material/tooltip"
import LoadingWrapper from "components/loading-wrapper"
import { ITEMS_PER_PAGE } from "./selectors"
import Row from "components/securities-table/row"
import EditableRow from "./components/editable-row"
import { getRealValue, numericValueRegex } from "components/factor-constraint-input/helpers"
import GroupingMenu from "components/grouping-menu"
import NewTrade from "./components/new-trade"
import { ROLLUP, SEARCHABLE_MENU_ITEM, SORT_ORDER, TABLE_GROUP_DROPDOWN, FILTER_ON_GROUP, LIST_ITEM_TEXT_OPTION, FILTER } from "helpers/shapes"
import { BASE } from "modules/ai-summary/shapes"
import { FIELDS } from "helpers/simulator"
import { CONTRIBUTOR, CONTRIBUTOR_WITH_ADV, FORECAST, SIMULATE_ACTION, TRADES } from "helpers/shapes/simulator"
import { COLUMN_GROUP } from "components/securities-table/row/shapes"
import { NORMALIZATION } from "helpers/segments"
import Filters from "components/analyze-header-filters"
import ExpansionMenu from "components/expansion-menu"
import Summary from "./components/summary"
import { OK, LOADING } from "helpers/data-statuses"
import withNavigate from "hocs/withNavigate"
import AISummarizer from "modules/ai-summary"
import { ACTIONS } from "modules/ai-summary/constants"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { computeAnalyticsOutput } from "modules/rebalance/helpers/queries/helpers"

import "./style.scss"

function synchronizeContainerOverflow() {
  const summary = document.getElementById("summary-table")
  const table = document.getElementById("metrics-table")
  if (!summary || !table) return

  // Scrollbars are set on the containers of the tables
  const summaryContainer = summary.parentNode
  const tableContainer = table.parentNode
  // If the table is scrollable, we need to set the overflow-y to scroll on the summary container to avoid the columns from shifting
  const overflow = tableContainer.scrollHeight > tableContainer.clientHeight ? "scroll" : "hidden"
  // Don't set the style property if it hasn't changed to avoid a re-paint
  if (summaryContainer.style.overflowY !== overflow) {
    summaryContainer.style.overflowY = overflow
  }
}

function synchronizeColumnWidths(widths, setWidths) {
  const summaryColumns = document.getElementById("summary-table-columns")
  const tableColumns = document.getElementById("metrics-table-columns")
  if (!summaryColumns || !tableColumns) { return }

  // Return early if the widths haven't changed, this avoids triggering a re-paint
  const newWidths = map(tableColumns.children, (node) => node.getBoundingClientRect().width)
  if (isEqual(widths, newWidths)) { return }
  setWidths(newWidths)

  // Iterate through the summaryColumns and set the widths according to the tableColumns widths
  for (let i = 0; i < newWidths.length; i++) {
    summaryColumns.children[i].style.width = `${newWidths[i]}px`
    summaryColumns.children[i].style.minWidth = `${newWidths[i]}px`
  }
}

function Rebalance({
  account,
  addNotification,
  aggregatedRow,
  availableRollups,
  base,
  baseUpdate,
  columns,
  combinedContributors,
  currentGrouping,
  data,
  decompositionType,
  dispatch,
  downloadData,
  earliestDate,
  error,
  experiment,
  factorsCategoriesHeader,
  forecastAndHorizon,
  forecasts,
  groupDropdown,
  hasIntradayData,
  idSort,
  includeNotHeld,
  intradayAsOf,
  intradayRefreshable,
  intradayBatchId,
  isActive,
  isActiveTradingDisabled,
  isIntraday,
  isIntradayRebalance,
  isIntradayRoute,
  isInvalidBetaInstrumentReason,
  isOpPro,
  isRunning,
  lastBatchId,
  lastRefresh,
  latestDate,
  loadExperiment,
  menuItems,
  modelId,
  newSimulateAction,
  numberOfContributors,
  numberOfTrades,
  openDialog,
  openFactorProfile,
  openSecurityProfile,
  openCompositeSecurity,
  page,
  portfolioAliasOrId,
  portfolioUUID,
  rebalance,
  reloadIntraday,
  resetRebalance,
  navigate,
  selectedCategory,
  selectedDate,
  selectedExperiment,
  selectedPositionWithAdv,
  selectSearchItem,
  setDecompositionType,
  setMetricType,
  setEditSecurity,
  setExperimentSelectedDate,
  setForecast,
  setGrouping,
  setIntradayMeta,
  setPage,
  setSelectedCategory,
  setSelectedDate,
  setSelectedGroup,
  setSorting,
  simulateActions,
  toggleNotHeld,
  tradeDownload,
  trades,
  unmodeledDates,
  workingEquity,
  toggleFilters,
  filter,
  updateFilters,
  addedFilters,
  filterOns,
  filtersVisible,
  currency,
  normalization,
  summaryRows,
  editing,
  metricType,
  metricTypeOptions,
  setExpansionMode,
  expand,
  attributionMethod,
  actionsBase,
  analyticsSegment,
  isAiTermsAccepted,
  startAddTrade,
}) {
  const [tradesOpened, setTradesOpened] = useState(false)
  const [noIntradayData, setNoIntradayData] = useState()
  const [widths, setWidths] = useState([])
  const tableRef = useRef()
  const summaryRef = useRef()
  const tradeDisabled = expand
  const filterButtonRef = useRef()

  useEffect(() => {
    synchronizeContainerOverflow()
    synchronizeColumnWidths(widths, setWidths)
  }, [data, columns, decompositionType, selectedCategory, currentGrouping, widths])

  useEffect(() => {
    // Commit the first lastRefresh (batch asOf time) to state to freeze the simulator in time
    if (!intradayAsOf) {
      setIntradayMeta({ asOf: lastRefresh, id: lastBatchId })
    }
  }, [intradayAsOf, setIntradayMeta, lastRefresh, lastBatchId])

  useEffect(() => {
    // When isIntradayRebalance is defined, it means trades have been made
    // If the intraday-ness of the route doesn't match the one from the rebalance, we reset
    if (!isUndefined(isIntradayRebalance) && isIntradayRebalance !== isIntradayRoute) {
      reloadIntraday()
      resetRebalance()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // If we are on the intraday route but are not on an intraday portfolio, we need to redirect to the regular simulator
    if (isIntradayRoute && isIntraday === false) {
      navigate(`/${account}/p/${portfolioAliasOrId}/rebalance/simulator`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntradayRoute, isIntraday])

  // emulate componentDidMount
  useEffect(() => {
    if (selectedExperiment) {
      loadExperiment()
    }

    if (baseUpdate) {
      rebalance()
    }

    // Because we don't automatically fetch new intraday data, we need to store the intial state of "existence"
    // to avoid the UI thinking that there is data when it actually has not been loaded yet
    setNoIntradayData(!hasIntradayData && isIntraday)
    // Cleanup state on unmount
    return () => setNoIntradayData(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the module is loaded in intraday mode and has no data, and we receive a position update,
  // Load the data automatically
  useEffect(() => {
    const loadedData = chain(data)
      .map()
      .flatten()
      .size()
      .value()

    if (isIntraday && hasIntradayData && !loadedData) {
      reloadIntraday()
    }
  }, [isIntraday, hasIntradayData, lastRefresh, data, reloadIntraday])

  // Display toaster when custom beta instrument is invalid
  useEffect(() => {
    if (isInvalidBetaInstrumentReason && decompositionType === "beta") {
      addNotification("warning", null, isInvalidBetaInstrumentReason)
    }
  }, [isInvalidBetaInstrumentReason, addNotification, decompositionType])

  const toggleTrades = () => {
    setTradesOpened(!tradesOpened)
  }

  const handleSetSelectedDate = date => {
    let setDateFn = setSelectedDate
    if (selectedExperiment) {
      setDateFn = setExperimentSelectedDate
    }
    if (numberOfTrades > 0) {
      openDialog({
        title: "Confirm Date Change & Clear All Trades",
        contentText: "Changing the date will reset your trades. Would you still like to proceed with the date change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            resetRebalance()
            setDateFn(date)
          },
        }],
      })
    } else {
      setDateFn(date)
    }
  }

  const toggleSaveExperiment = () => {
    openDialog({
      title: "Save Experiment",
      content: <SaveExperiment experiment={experiment} isOneDayExperiment={selectedDate === latestDate} portfolioId={portfolioUUID} portfolioAlias={portfolioAliasOrId} />,
    })
  }

  const applyForecast = (data) => {
    setForecast({ forecast: data.forecast, horizon: data.horizon })
  }

  const openForecastEdit = () => {
    openDialog({
      title: "Edit Forecast",
      content: <EditForecast
        applyForecast={applyForecast}
        selectedForecast={forecastAndHorizon.forecast}
        horizon={forecastAndHorizon.horizon}
        forecasts={forecasts}
        isOpPro={isOpPro}
      />,
    })
  }

  const handleOnChange = (security, original, newValue, row, field) => {
    const trimmedValue = replace(newValue, /[\s$%]/gi, "")
    let value = field === FIELDS.economicExposure ? getRealValue(trimmedValue) : trimmedValue
    // Validate value
    if (numericValueRegex.test(value)) {
      value = parseFloat(value)
      if (row === "delta") {
        switch (field) {
          case FIELDS.economicExposure:
            value = get(security, "original.economicExposure", 0) + value
            break
          case FIELDS.positionPercentEquity:
            value = get(security, "original.positionPercentEquity", 0) + value
            break
          case FIELDS.activePercentEquity:
            value = get(security, "original.activePercentEquity", 0) + value
            break
        }
      }

      const tradedPosition = {
        ...security,
        id: security.originalId,
      }

      if (!tradeDisabled) {
        newSimulateAction(tradedPosition, field, value, false, false)
      }
    }
    setEditSecurity(null)
  }

  const [editingRow, setEditingRow] = useState("rebalanced")
  const [editingField, setEditingField] = useState(FIELDS.economicExposure)

  const rebalanced = !isEmpty(simulateActions)
  const customReloadButton = <IntradayRebalanceReload type="intraday-simulator">
    {intradayRefreshable && <OPTooltip title="New data is available"><IconButton color="primary" className="intraday-reload-button grow" size="large"><RefreshIcon /></IconButton></OPTooltip>}
  </IntradayRebalanceReload>

  const handleOnRowClick = useCallback((e, item) => {
    // Because the click event is bound to the row element <tr>, the event target can be a variety of different elements but we want
    // to know if the click was done inside of the identifier cell
    const isIdentifier = includes(e?.target?.closest("td")?.classList, "identifier")
    const notOriginalRow = includes(["rebalanced", "delta"], item.id)

    if (item.isGroup && isIdentifier && !notOriginalRow) {
      setSelectedGroup(item.id)
      return
    }
    if (tradeDisabled) return
    // If we are currently editing the security, handle the click events at the Cell level not the Row level
    if (item.isEditable) return
    // The data-id is set on the span inside the cell, but the click event can happen on the td itself.
    // If target doesn't have the attribute data-id, attempt to find it in its children
    const columnId = e?.target?.getAttribute("data-id") ?? e.target.querySelectorAll("span[data-id]")[0]?.getAttribute("data-id")
    if (!columnId) return
    setEditingField(columnId)
    setEditSecurity(item)
  }, [setEditSecurity, setSelectedGroup, tradeDisabled])

  const handleScrollSummary = scroll => {
    if (!summaryRef.current) return
    summaryRef.current.scrollLeft = scroll.target.scrollLeft
  }

  const handleScrollTable = scroll => {
    if (!tableRef.current) return
    tableRef.current.scrollLeft = scroll.target.scrollLeft
  }

  const collapseRow = () => {
    setEditSecurity(null)
    setEditingRow("rebalanced")
    setEditingField(null)
  }

  const handleTableClick = (e) => {
    const isEditableCell = includes(["economicExposure", "positionPercentEquity", "activePercentEquity"], e?.target?.getAttribute("data-id"))
    const row = e.target.closest("tr")
    const isEditableRow = row && row.classList.contains("is-editing")

    if (!isEditableRow && !isEditableCell && !isEmpty(editing)) {
      collapseRow()
    }
  }

  const { analyticsOutput } = computeAnalyticsOutput({ isActive, base, analyticsSegment, expand })
  const Summarizer = isAiTermsAccepted ? <AISummarizer
    key="aiSummarizer"
    {...{
      tooltip: "Open Simulator Summary",
      action: ACTIONS.SIMULATOR_SUMMARY,
      account,
      modelId,
      resourceId: portfolioAliasOrId,
      resourceType: "PORTFOLIO",
      ...(isActive && { base }),
      startDate: selectedDate,
      endDate: selectedDate,
      extraParameters: {
        attributionMethod,
        actions: simulateActions,
        actionsBase,
        batchId: intradayBatchId,
        analyticsOutput,
      },
    }}
  /> : null

  return <>
    <div className="modules_rebalance">
      <Card square elevation={1} id="rebalance-header">
        <div className="rebalance-settings">
          <div className={classNames("rebalance-date-wrapper", { "no-forecast": isIntraday })}>
            {isIntraday ? <div className="components_date-selector intraday">
              <div className="title">Intraday</div>
              <IntradayRefresh hasData={hasIntradayData} lastRefresh={intradayAsOf} customButton={customReloadButton} />
            </div> : <DateSelector {...{ earliestDate, latestDate, selectedDate, exclusionMap: unmodeledDates, onChange: handleSetSelectedDate }} />}
            {!isIntraday && <ForecastSelector forecast={get(forecastAndHorizon, "forecast")} openForecastEdit={openForecastEdit} isOpPro={isOpPro} />}
          </div>
          <div className="rebalance-data-type-wrapper">
            <div className="rebalance-options-wrapper">
              {!isIntraday && <ShowNotHeldButton {...{ includeNotHeld, toggleNotHeld, disabled: !isActive }} />}
              <TradesButton {...{ toggleTrades, numberOfTrades }} />
              {setGrouping && <GroupingMenu {...{ availableRollups, setGrouping, currentGrouping }} />}
              {!isIntraday && <SaveExperimentButton {...{ toggleSaveExperiment }} />}
              <ResetButton {...{ resetRebalance, rebalanced, error, noIntradayData }} />
              <ExpansionMenu {...{ setExpansionMode, isExpanded: expand }} />
            </div>
          </div>
          <Divider orientation="vertical" flexItem />
          <OPTooltip title="Filter positions">
            <IconButton
              ref={filterButtonRef}
              onClick={toggleFilters}
              className={classNames("filters-toggle", { active: filtersVisible })}
              size="large"
            >
              <Badge overlap="rectangular" color="primary" invisible={filtersVisible || addedFilters?.length === 0} badgeContent={addedFilters?.length}>
                <FilterList />
              </Badge>
            </IconButton>
          </OPTooltip>
        </div>
        <div className="rebalance-filters">
          {filtersVisible && <Filters
            {...{
              heapAddFilterId: "rebalance-filter",
              account,
              modelId,
              startDate: selectedDate,
              endDate: selectedDate,
              filter,
              updateFilters,
              addedFilters,
              normalization,
              disableNormalization: true,
              disableNormalizationTooltip: "Standalone is not available in simulator",
              setNormalization: noop,
              showFilterScope: true,
              isFilterScopeUnavailable: true,
              filterOns,
              currency,
              noSpinner: true,
              isExpanded: expand,
              filtersVisible,
              toggleFilters,
              anchorElement: filterButtonRef.current,
            }}
          />}
        </div>
      </Card>

      {error && <Error />}
      {noIntradayData && <Paper className="rebalance-content"><NoData /></Paper>}
      {!error && !noIntradayData && <>
        <Paper square className="header">
          <AssetsTableHeader
            {...{
              downloadData,
              menuItems,
              selectedSubView: selectedCategory || get(factorsCategoriesHeader, "0.id"),
              selectedView: decompositionType,
              setSelectedSubView: setSelectedCategory,
              setSelectedView: (type) => setDecompositionType(type),
              metricTypeOptions,
              metricType,
              setMetricType: (type) => setMetricType(type),
              subViewOptions: factorsCategoriesHeader,
              setExpansionMode,
              downloadButtonTooltip: "Download the table data",
              customActions: [
                Summarizer,
                <NewTrade
                  key="newTrade"
                  {...{
                    account,
                    contributors: combinedContributors,
                    currentGrouping,
                    isActive,
                    isActiveTradingDisabled,
                    isRunning,
                    modelId,
                    newSimulateAction,
                    selectedDate,
                    selectedPositionWithAdv,
                    selectSearchItem,
                    workingEquity,
                    tradeDisabledReason: expand ? "All trading is disabled when expanded." : null,
                    Summarizer,
                    onAddTradeClicked: startAddTrade,
                  }}
                />],
            }}
          />
          {expand && <Alert severity="info">All trading is disabled when expanded. Click <a href="#" onClick={() => setExpansionMode(false)}>here</a> to collapse composites.</Alert>}
        </Paper>
        <LoadingWrapper status={[isRunning ? LOADING : OK]}>
          <Summary
            ref={summaryRef}
            onScroll={handleScrollTable}
            id="summary-table"
            {...{
              columnGroups: columns,
              data: summaryRows,
            }}
          />
          <Paper square className="rebalance-content">
            <ClickAwayListener onClickAway={collapseRow}>
              <Table
                id="metrics-table"
                ref={tableRef}
                onScroll={handleScrollSummary}
                onClick={handleTableClick}
                {...{
                  aggregatedRow,
                  className: classNames("rebalance-table", { grouped: currentGrouping, tradeDisabled }),
                  allowIdSort: true,
                  columns,
                  count: numberOfContributors,
                  disableColumnsSelection: true,
                  disableRowSelection: true,
                  dispatch,
                  foundSecurities: data,
                  idSort,
                  isActive,
                  openFactorProfile,
                  openSecurityProfile,
                  openCompositeSecurity,
                  outsidePagination: true,
                  page,
                  rowsPerPage: ITEMS_PER_PAGE,
                  selectedSecurities: {},
                  setPage,
                  setSort: setSorting,
                  status: "ok",
                  groupDropdown,
                  disableCategories: true,
                }}
              >
                {map(data, (security, index) => {
                  const props = {
                    firstRow: index === 0,
                    disableRowSelection: true,
                    openSecurityProfile,
                    openFactorProfile,
                    openCompositeSecurity,
                    item: security,
                    columns,
                    dispatch,
                    isActive,
                    isActiveTradingDisabled,
                    setEditSecurity,
                    editingRow,
                    editingField,
                    setEditingField,
                    setEditingRow,
                    onRowClick: handleOnRowClick,
                    onChange: tradeDisabled ? noop : handleOnChange,
                    isOriginalRow: !includes(["rebalanced", "delta"], security.id),
                    className: includes(["rebalanced", "delta"], security.id) ? security.id : "original",
                  }
                  return security.isEditable ? <EditableRow key={security.id} {...props} /> : <Row key={security.id} {...props} />
                },
                )}
              </Table>
            </ClickAwayListener>
          </Paper>
        </LoadingWrapper>
      </>}
    </div>
    <RebalanceTrades {...{ toggleTrades, tradesOpened, trades, csvRows: tradeDownload, selectedDate, toggleSaveExperiment, saveDisabled: isIntraday }} />
  </>
}

Rebalance.propTypes = {
  account: PropTypes.string.isRequired,
  aggregatedRow: CONTRIBUTOR,
  availableRollups: PropTypes.arrayOf(ROLLUP),
  baseUpdate: PropTypes.bool,
  base: PropTypes.object,
  simulateActions: PropTypes.arrayOf(SIMULATE_ACTION),
  columns: PropTypes.arrayOf(COLUMN_GROUP),
  combinedContributors: PropTypes.objectOf(CONTRIBUTOR),
  currentGrouping: PropTypes.object,
  data: PropTypes.arrayOf(CONTRIBUTOR).isRequired,
  decompositionType: PropTypes.string,
  downloadData: PropTypes.shape({
    fileName: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.array).isRequired,
  }).isRequired,
  earliestDate: PropTypes.string.isRequired,
  error: PropTypes.any,
  experiment: PropTypes.object.isRequired,
  factorsCategoriesHeader: PropTypes.array,
  fileName: PropTypes.string,
  forecastAndHorizon: PropTypes.shape({
    forecast: FORECAST,
    horizon: PropTypes.number,
  }),
  forecasts: PropTypes.arrayOf(FORECAST).isRequired,
  groupDropdown: TABLE_GROUP_DROPDOWN,
  hasIntradayData: PropTypes.bool,
  idSort: PropTypes.string,
  includeNotHeld: PropTypes.bool.isRequired,
  intradayAsOf: PropTypes.string,
  intradayRefreshable: PropTypes.bool,
  intradayBatchId: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isActiveTradingDisabled: PropTypes.bool,
  isIntraday: PropTypes.bool, // This is a prop coming from a selector combining the route prop and the portfolio intradayEnabled
  isIntradayRebalance: PropTypes.bool,
  isIntradayRoute: PropTypes.bool.isRequired,
  isInvalidBetaInstrumentReason: PropTypes.string,
  isOpPro: PropTypes.bool.isRequired,
  isRunning: PropTypes.bool,
  lastBatchId: PropTypes.string,
  lastRefresh: PropTypes.string, // eg. "8:52:06 AM"
  latestDate: PropTypes.string.isRequired,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  modelId: PropTypes.string.isRequired,
  numberOfContributors: PropTypes.number.isRequired,
  numberOfTrades: PropTypes.number,
  order: SORT_ORDER,
  page: PropTypes.number.isRequired,
  portfolioAliasOrId: PropTypes.string.isRequired,
  portfolioUUID: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  selectedCategory: PropTypes.string,
  selectedDate: PropTypes.string.isRequired,
  selectedExperiment: PropTypes.object,
  selectedPositionWithAdv: CONTRIBUTOR_WITH_ADV,
  status: PropTypes.string,
  tradeDownload: PropTypes.arrayOf(PropTypes.array),
  trades: TRADES,
  unmodeledDates: PropTypes.object.isRequired,
  workingEquity: PropTypes.number,
  editing: CONTRIBUTOR,
  metricType: PropTypes.string,
  metricTypeOptions: PropTypes.arrayOf(LIST_ITEM_TEXT_OPTION),
  filter: PropTypes.object,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  summaryRows: PropTypes.arrayOf(CONTRIBUTOR),
  expand: PropTypes.bool.isRequired,
  // Actions ------------------------------
  addNotification: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  loadExperiment: PropTypes.func.isRequired,
  newSimulateAction: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  rebalance: PropTypes.func.isRequired,
  reloadIntraday: PropTypes.func.isRequired,
  resetRebalance: PropTypes.func.isRequired,
  selectSearchItem: PropTypes.func.isRequired,
  setDecompositionType: PropTypes.func.isRequired,
  setMetricType: PropTypes.func.isRequired,
  setEditSecurity: PropTypes.func.isRequired,
  setExperimentSelectedDate: PropTypes.func.isRequired,
  setForecast: PropTypes.func.isRequired,
  setGrouping: PropTypes.func.isRequired,
  setIntradayMeta: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSelectedCategory: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  setSelectedGroup: PropTypes.func.isRequired,
  setSorting: PropTypes.func.isRequired,
  toggleNotHeld: PropTypes.func.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  resetEditingFilter: PropTypes.func.isRequired,
  editingFilter: PropTypes.object,
  editFilter: PropTypes.func.isRequired,
  setExpansionMode: PropTypes.func.isRequired,
  attributionMethod: PropTypes.oneOf(Object.values(ATTRIBUTION_METHODS)),
  actionsBase: BASE,
  analyticsSegment: PropTypes.object,
  isAiTermsAccepted: PropTypes.bool.isRequired,
  updateFilters: PropTypes.func.isRequired,
  addedFilters: PropTypes.arrayOf(FILTER).isRequired,
  startAddTrade: PropTypes.func.isRequired,
}

export default withNavigate(Rebalance)
